import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Container, Title, Text, SegmentedControl, SimpleGrid, Card, Button,
  Group, Stack, List, ThemeIcon, Badge, Center, Loader, Anchor,
} from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { usePlan } from "../hooks/usePlan";
import { checkoutSubscription, customerPortal } from "./pricingApi";

const ENTERPRISE_EMAIL = "sales@veft.ai";

// Tier shape: names are brand terms (kept verbatim, they also match the backend Plan claim);
// blurbs and feature labels are i18n keys resolved at render time. Numeric limits mirror
// VeftPlanLimitationsService. Feature labels shared across tiers reuse the same key.
const TIERS = [
  {
    key: "Trial",
    name: "Trial",
    blurbKey: "plans_trial_blurb",
    price: { monthly: 0, annual: 0 },
    features: [
      { key: "plans_trial_feat_candidates", ok: true },
      { key: "plans_trial_feat_user", ok: true },
      { key: "plans_trial_feat_cv", ok: true },
      { key: "plans_feat_bulk_import", ok: false },
      { key: "plans_feat_ai_api", ok: false },
      { key: "plans_trial_feat_buy_credits", ok: false },
    ],
  },
  {
    key: "Pro",
    name: "Pro",
    highlight: true,
    blurbKey: "plans_pro_blurb",
    // Same number in either currency (€500 = $500); annual = 10× monthly (2 months free).
    // Stripe is the source of truth for the actual charge — keep this in sync with the
    // veft_pro_* prices configured there.
    price: { monthly: 500, annual: 5000 },
    features: [
      { key: "plans_pro_feat_candidates", ok: true },
      { key: "plans_pro_feat_users", ok: true },
      { key: "plans_feat_bulk_import", ok: true },
      { key: "plans_feat_ai_api", ok: true },
      { key: "plans_pro_feat_buy_credits", ok: true },
      { key: "plans_feat_custom_templates", ok: false },
    ],
  },
  {
    key: "Enterprise",
    name: "Enterprise",
    blurbKey: "plans_enterprise_blurb",
    price: null, // contact sales
    features: [
      { key: "plans_ent_feat_unlimited", ok: true },
      { key: "plans_ent_feat_seats", ok: true },
      { key: "plans_feat_custom_templates", ok: true },
      { key: "plans_ent_feat_sso", ok: true },
      { key: "plans_ent_feat_credits", ok: true },
    ],
  },
];

export default function PlansPage() {
  const { t, i18n } = useTranslation("Veft");
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState(null);
  const { plan, loaded, hasBillingAccount } = usePlan();
  // Display symbol only — the amount is identical in EUR and USD. Stripe Checkout
  // resolves the real charged currency by the customer's billing country.
  const currency = (i18n.resolvedLanguage || i18n.language || "en")
    .toLowerCase()
    .startsWith("de")
    ? "€"
    : "$";

  const startCheckout = async (planName) => {
    setBusy(planName);
    try {
      const res = await checkoutSubscription({ planName, interval });
      if (res?.redirectUrl) window.location.href = res.redirectUrl;
      else throw new Error("No checkout URL returned");
    } catch (e) {
      notifications.show({ color: "red", title: t("plans_checkout_failed"), message: String(e.message || e) });
      setBusy(null);
    }
  };

  const openPortal = async () => {
    setBusy("portal");
    try {
      const res = await customerPortal();
      if (res?.redirectUrl) window.location.href = res.redirectUrl;
      else throw new Error("No portal URL returned");
    } catch (e) {
      notifications.show({ color: "red", title: t("plans_portal_failed"), message: String(e.message || e) });
      setBusy(null);
    }
  };

  const renderCta = (tier) => {
    const isCurrent = loaded && plan === tier.key;

    if (tier.key === "Trial") {
      return isCurrent
        ? <Badge size="lg" variant="light" color="gray">{t("plans_cta_current")}</Badge>
        : <Button variant="default" disabled fullWidth>{t("plans_cta_included")}</Button>;
    }
    if (tier.key === "Enterprise") {
      return (
        <Button component="a" href={`mailto:${ENTERPRISE_EMAIL}?subject=Veft%20Enterprise`}
          variant={isCurrent ? "light" : "outline"} fullWidth>
          {isCurrent ? t("plans_cta_contact_current") : t("plans_cta_contact")}
        </Button>
      );
    }
    // Pro — only a tenant with a real Stripe customer can use the billing portal. A
    // plan granted manually by an admin (Plan claim, no customer) must be able to start
    // a real checkout instead of hitting "Manage subscription", which would 400.
    if (isCurrent && hasBillingAccount) {
      return (
        <Button variant="light" fullWidth loading={busy === "portal"} onClick={openPortal}>
          {t("plans_cta_manage")}
        </Button>
      );
    }
    return (
      <Button fullWidth loading={busy === tier.key} onClick={() => startCheckout(tier.key)}>
        {isCurrent
          ? t("plans_cta_subscribe", { name: tier.name })
          : t("plans_cta_choose", { name: tier.name })}
      </Button>
    );
  };

  return (
    <Container size="lg" py="xl">
      <Stack align="center" gap="xs" mb="lg">
        <Title order={2}>{t("plans_title")}</Title>
        <Text c="dimmed">{t("plans_subtitle")}</Text>
        {loaded && (
          <Badge variant="light" size="lg" mt="xs">{t("plans_current_badge", { plan })}</Badge>
        )}
      </Stack>

      <Center mb="xl">
        <SegmentedControl
          value={interval}
          onChange={setInterval}
          data={[
            { label: t("plans_interval_monthly"), value: "monthly" },
            { label: t("plans_interval_annual"), value: "annual" },
          ]}
        />
      </Center>

      {!loaded && (
        <Center my="md"><Loader size="sm" /></Center>
      )}

      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="lg">
        {TIERS.map((tier) => (
          <Card key={tier.key} withBorder radius="md" padding="lg"
            style={tier.highlight ? { borderColor: "var(--mantine-color-blue-5)", borderWidth: 2 } : undefined}>
            <Stack gap="sm" h="100%">
              <Group justify="space-between">
                <Title order={3}>{tier.name}</Title>
                {tier.highlight && <Badge color="blue">{t("plans_most_popular")}</Badge>}
              </Group>
              <Text size="sm" c="dimmed" mih={40}>{t(tier.blurbKey)}</Text>

              <div>
                {tier.price == null ? (
                  <Text fw={700} fz={28}>{t("plans_lets_talk")}</Text>
                ) : tier.price.monthly === 0 ? (
                  <Text fw={700} fz={28}>{t("plans_free")}</Text>
                ) : (
                  <Group gap={4} align="baseline">
                    <Text fw={700} fz={32}>
                      {currency}{interval === "annual" ? Math.round(tier.price.annual / 12) : tier.price.monthly}
                    </Text>
                    <Text c="dimmed" size="sm">{t("plans_per_month")}</Text>
                  </Group>
                )}
                {tier.price?.annual > 0 && interval === "annual" && (
                  <Text size="xs" c="dimmed">{t("plans_billed_yearly", { currency, amount: tier.price.annual })}</Text>
                )}
              </div>

              <List spacing={6} size="sm" center mt="xs" style={{ flexGrow: 1 }}>
                {tier.features.map((feat) => (
                  <List.Item key={feat.key}
                    icon={
                      <ThemeIcon color={feat.ok ? "teal" : "gray"} variant="light" size={20} radius="xl">
                        {feat.ok ? <IconCheck size={12} /> : <IconX size={12} />}
                      </ThemeIcon>
                    }>
                    <Text size="sm" c={feat.ok ? undefined : "dimmed"}>{t(feat.key)}</Text>
                  </List.Item>
                ))}
              </List>

              {renderCta(tier)}
            </Stack>
          </Card>
        ))}
      </SimpleGrid>

      <Text ta="center" c="dimmed" size="sm" mt="xl">
        {t("plans_footer_more_credits")} <Anchor href="/credits">{t("plans_footer_buy_packs")}</Anchor>.{" "}
        {t("plans_footer_questions")} <Anchor href={`mailto:${ENTERPRISE_EMAIL}`}>{t("plans_footer_contact_us")}</Anchor>.
      </Text>
    </Container>
  );
}
